/* @flow */
/**
 * External dependencies
 */
import styled, { css } from 'styled-components';

/**
 * Internal dependencies
 */
import { Root } from '../../style';
import focus from '../../utils/accessible-focus';

/**
 * Style
 */
export const FullScreenPlayer = styled( Root )`
	background-color: #000;
	overflow: hidden;
	position: absolute;
	z-index: 10;
	opacity: ${ props => props.isMainWindow ? 1 : 0 };

	${ props => props.isIntro && css`
		z-index: 20;
		position: fixed;
	` }

	> .video-frame {
		left: 50%;
		position: absolute;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	> .image-frame {
		background-size: cover;
		height: 100%;
		left: 0;
		position: absolute;
		top: 0;
		width: 100%;
	}
`;

export const PlayButton = styled.button`
	background: transparent;
	border-radius: 50%;
	border: 0;
	color: #fff;
	cursor: pointer;
	height: 100px;
	outline: none;
	padding: 0;
	position: absolute;
	left: 50%;
	top: 50%;
	width: 100px;
	z-index: 10;
	margin-top: -50px;
	margin-left: -50px;

	${ focus`
		background: ${ props => props.theme.background };
		color: ${ props => props.theme.color };
	` }
`;

export const Progress = styled.div`
	background: linear-gradient(to bottom, rgba(30,87,153,0) 0%,rgba(0,0,0,1) 100%);
	position: absolute;
	width: 100%;
	bottom: 0;
	left: 0;
	height: 90px;
	opacity: ${ props => props.visible ? 1 : 0 };
	transition: opacity 350ms;
	z-index: ${ props => props.visible ? 20 : 1 };

	.progress-pause {
		background-color: transparent;
		border: 0;
		bottom: 0;
		cursor: pointer;
		color: #fff;
		height: 84px;
		left: 0;
		outline: none;
		padding: 0;
		position: absolute;
		width: 60px;

		svg {
			width: 20px;
		}
	}

	.dot-inner {
		bottom: 20px;
		height: 28px;
		left: 60px;
		position: absolute;
		width: 0;
		width: calc(100% - 130px);
		z-index: 2;
	}

	.dot-time {
		bottom: 35px;
		color: #fff;
		left: 50%;
		position: absolute;
		transform: translate(-50%, 0);
		white-space: nowrap;
	}

	.dot-inner-inner {
		position: absolute;
		left: 0;
		top: 0;
		width: calc(100% - 28px);
	}

	.dot-range {
		width: 100%;
		position: relative;
		z-index: 10;
	}

	[type='range'] {
		-webkit-appearance: none;
		background: transparent;
		margin: 14px 0;
		width: 100%;
	}

	[type='range']::-moz-focus-outer {
		border: 0;
	}

	[type='range']:focus {
		outline: 0;
	}

	[type='range']:focus::-webkit-slider-runnable-track {
		background: white;
	}

	[type='range']:focus::-ms-fill-lower {
		background: #fff;
	}

	[type='range']:focus::-ms-fill-upper {
		background: white;
	}

	[type='range']::-webkit-slider-runnable-track {
		cursor: default;
		height: 2px;
		transition: all .2s ease;
		width: 100%;
		box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2), 0 0 1px rgba(13, 13, 13, 0.2);
		background: #fff;
		border: 0px solid #cfd8dc;
		border-radius: 0px;
	}

	[type='range']::-webkit-slider-thumb {
		box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.2), 0 0 4px rgba(13, 13, 13, 0.2);
		background: ${ props => props.theme.backgroundDark };
		border: 0px solid #eceff1;
		border-radius: 100px;
		cursor: default;
		height: 28px;
		width: 28px;
		-webkit-appearance: none;
		margin-top: -13px;
	}

	[type='range']::-moz-range-track {
		box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2), 0 0 1px rgba(13, 13, 13, 0.2);
		cursor: default;
		height: 2px;
		transition: all .2s ease;
		width: 100%;
		background: #fff;
		border: 0px solid #cfd8dc;
		border-radius: 0px;
		height: 1px;
	}

	[type='range']::-moz-range-thumb {
		box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.2), 0 0 4px rgba(13, 13, 13, 0.2);
		background: ${ props => props.theme.backgroundDark };
		border: 0px solid #eceff1;
		border-radius: 100px;
		cursor: default;
		height: 24px;
		width: 24px;
	}

	[type='range']::-ms-track {
		cursor: default;
		height: 2px;
		transition: all .2s ease;
		width: 100%;
		background: transparent;
		border-color: transparent;
		border-width: 14px 0;
		color: transparent;
	}

	[type='range']::-ms-fill-lower {
		box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2), 0 0 1px rgba(13, 13, 13, 0.2);
		background: #f2f2f2;
		border: 0px solid #cfd8dc;
		border-radius: 0px;
	}

	[type='range']::-ms-fill-upper {
		box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2), 0 0 1px rgba(13, 13, 13, 0.2);
		background: #fff;
		border: 0px solid #cfd8dc;
		border-radius: 0px;
	}

	[type='range']::-ms-thumb {
		box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.2), 0 0 4px rgba(13, 13, 13, 0.2);
		background: ${ props => props.theme.backgroundDark };
		border: 0px solid #eceff1;
		border-radius: 100px;
		cursor: default;
		height: 24px;
		width: 24px;
		margin-top: 0.5px;
	}

	[type='range']:disabled::-webkit-slider-thumb {
		cursor: not-allowed;
	}

	[type='range']:disabled::-moz-range-thumb {
		cursor: not-allowed;
	}

	[type='range']:disabled::-ms-thumb {
		cursor: not-allowed;
	}

	[type='range']:disabled::-webkit-slider-runnable-track {
		cursor: not-allowed;
	}

	[type='range']:disabled::-ms-fill-lower {
		cursor: not-allowed;
	}

	[type='range']:disabled::-ms-fill-upper {
		cursor: not-allowed;
	}
`;

export const Dot = styled.span.attrs( {
	style: ( { played } ) => ( {
		left: `${ ( played * 100 ) }%`,
	} ),
} )`
	position: absolute;
	top: 0;
	height: 28px;
	width: 28px;
`;

export const Time = styled.div`
	bottom: 24px;
	color: #fff;
	position: absolute;
	right: 20px;
`;

export const OverLay = styled.div`
	left: 0;
	height: 100vh;
	position: absolute;
	top: 0;
	width: 100%;
	z-index: 10;
`;

export const PauseScreen = styled.div`
	background-color: rgba(0, 0, 0, 0.75);
	color: #fff;
	font-size: 20px;
	left: 0;
	height: 100vh;
	position: absolute;
	text-align: center;
	top: 0;
	width: 100%;
	z-index: 10;

	> div {
		left: 50%;
		margin: 0 auto;
		max-width: 375px;
		padding: 50px;
		position: absolute;
		top: 50%;
		transform: translate(-50%, -50%);
		width: 100%;
	}
`;



// WEBPACK FOOTER //
// ./src/views/video/style.js